import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { infoBubble } from "../../helpers/infoBubble";
import { datalabelFont, legendPad, onResize, tooltipTitleFont } from './chartJsPlugins';
import { fillMissingDataWithoutNotListed, GrowthMetricSampleRow, upperizeStringify } from './chartUtils';
import { generateChartId, htmlLegendPlugin } from './configs';


// @ts-ignore
window.initStudentGrowthSimple = (function ($, blockId, config, schoolResponse, networkResponse, districtResponse, bubbleConfigs, notUseDefaultTooltip) {
    console.log('school data', schoolResponse)
    console.log('netowrk data', networkResponse)
    console.log('district data', districtResponse)

    // MIKES SANDBOX
    const firstOrNull = (arr: any[]) => {
        return arr && arr.length > 0 ? arr[0] : null;
    }

    type FormattedSourceData = {
        [prop: string]: { // Demographic
            [prop: string]: { // category in the demographic
                [prop: string]: { // year
                    school?: SourceDatapoint;
                    network?: SourceDatapoint;
                    district?: SourceDatapoint;
                }
            }
        }

    }

    type SourceDatapoint = {
        EffectSize: number;
        EffectSizeText: string;
        NoData: string;
        NotStatSignificant: number; // pretty sure this is functionally a bool
    }

    type ChartData = {
        data: {
            labels: string[], // school years, y axis
            datasets: ChartDataset[]
        }
    };

    type ChartDataset = {
        label: string; // School / Network / District
        data: string[]; // ordered set of the above. specific to year + demo + demo category
        backgroundColor?: string;
        hidden?: boolean;
        datalabels: {
            color: string;
        },
        sideLabels: string[];
    }

    // pull out as a utlity function: this has no dependencies
    const deepMerge = (target, source) => {
        // If source isn't an object, just return it
        if (typeof source !== 'object' || source === null) {
            return source;
        }

        // If target isn't an object, replace it with a new one
        if (typeof target !== 'object' || target === null) {
            target = Array.isArray(source) ? [] : {};
        }

        for (const key of Object.keys(source)) {
            const srcValue = source[key];
            const tgtValue = target[key];

            // If both values are objects, merge recursively
            if (typeof srcValue === 'object' && srcValue !== null) {
                target[key] = deepMerge(tgtValue, srcValue);
            } else {
                // Otherwise, overwrite
                target[key] = srcValue;
            }
        }
        return target;
    }

    // scoping helpers
    const scopedId = (id: string) => `${blockId}-${id}`;
    const parentScope = '.' + scopedId('metric-block');
    const gradeField = ["Cohort Graduation 4-Year Rate", "Cohort Graduation 5-Year Rate"].includes(config.metricTitle) ? "Grade Entered CPS HS" : "Grade Level";

    // this uses property names as keys which is nicer to look at and use (not filtering at every consumption),
    // but more fragile. Once everything is in place, decide if it's better to move to arrays and helper functions
    // where appropriate
    const sortResponseData = (apiResponse, propName: string): FormattedSourceData => {
        // assumes all demographics are in all years
        const years = apiResponse.SchoolYears.map(y => y.SchoolYear);
        // bad big O performance, but the data coming in is pretty much inverted. can optimize if needed
        // but I don't think this is a bottleneck.
        // if it is bad, this can be unrolled to grab all demographics and categories and do a single pass
        const yearData = apiResponse.SchoolYears.map(year => {
            return year.Categories.reduce((acc, val) => {
                if (!acc.hasOwnProperty(val.Category)) {
                    acc[val.Category] = [];
                }
                if (!acc[val.Category].hasOwnProperty(val.CategoryDescription)) {
                    acc[val.Category][val.CategoryDescription] = {};
                }
                const data = {
                    [propName]: val
                };
                acc[val.Category][val.CategoryDescription][year.SchoolYear] = data;

                return acc;
            }, {});
        });
        return yearData.reduce((acc, val) => {
            return deepMerge(acc, val);
        }, {});
    }

    const getSortedData = () => {
        // @TODO these are all school while the data is broken, fix it
        const school = sortResponseData(schoolResponse, 'school');
        const network = sortResponseData(networkResponse, 'network');
        const district = sortResponseData(districtResponse, 'district');

        // yuck, but fine for now
        let data = {};
        data = deepMerge(data, school);
        data = deepMerge(data, network);
        data = deepMerge(data, district);

        // fill data that we know is sometimes missing.

        return data;
    }
    //console.log('sorted', getSortedData());

    // this should be rebuilt, but validating the giant html blob is harder than key mapping here
    const trends = [
        {
            var: 'absenceTrendDisadvantage1',
            id: scopedId('absence-all'),
            tableId: scopedId('chart-absence-all'),
            name: 'ALL-All Students',
            demographic: 'ALL',
            category: 'All Students'
        },
        {
            var: 'absenceTrendDisadvantage1',
            id: scopedId('economic-disadvantage1'),
            tableId: scopedId('chart-economic-disadvantage1'),
            name: 'Economic Disadvantage-Economic Disadvantage',
            demographic: 'Economic Disadvantage',
            category: 'Economic Disadvantage'
        },
        {
            var: 'absenceTrendDisadvantage2',
            id: scopedId('economic-disadvantage2'),
            tableId: scopedId('chart-economic-disadvantage2'),
            name: 'Economic Disadvantage-Non-Economic Disadvantage',
            demographic: 'Economic Disadvantage',
            category: 'Non-Economic Disadvantage'
        },
        {
            var: 'absenceTrendEls1',
            id: scopedId('english-learner-status1'),
            tableId: scopedId('chart-english-learner-status1'),
            name: 'English Learner Status-English Learner',
            demographic: 'English Learner Status',
            category: 'English Learner'
        },
        {
            var: 'absenceTrendEls2',
            id: scopedId('english-learner-status2'),
            tableId: scopedId('chart-english-learner-status2'),
            name: 'English Learner Status-Non-English Learner',
            demographic: 'English Learner Status',
            category: 'Non-English Learner'
        },
        {
            var: 'absenceTrendGender1',
            id: scopedId('gender1'),
            tableId: scopedId('chart-gender1'),
            name: 'Gender-Female',
            demographic: 'Gender',
            category: 'Female'
        },
        {
            var: 'absenceTrendGender2',
            id: scopedId('gender2'),
            tableId: scopedId('chart-gender2'),
            name: 'Gender-Male',
            demographic: 'Gender',
            category: 'Male'
        },
        {
            var: 'absenceTrendGender3',
            id: scopedId('gender3'),
            tableId: scopedId('chart-gender3'),
            name: 'Gender-Non-Binary',
            demographic: 'Gender',
            category: 'Non-Binary'
        },
        // @TODO this is where `getGradeCharts()` was, if it matters

        {
            var: 'iepStatus',
            id: scopedId('iep-status1'),
            tableId: scopedId('chart-iep-status1'),
            name: 'IEP Status-IEP',
            demographic: 'IEP Status',
            category: 'IEP'
        },
        {
            var: 'iepStatus2',
            id: scopedId('iep-status2'),
            tableId: scopedId('chart-iep-status2'),
            name: 'IEP Status-Non-IEP',
            demographic: 'IEP Status',
            category: 'Non-IEP'
        },

        {
            var: 'absenceTrendRace1',
            id: scopedId('race-ethnicity1'),
            tableId: scopedId('chart-race-ethnicity1'),
            name: 'Race/Ethnicity-Asian',
            demographic: 'Race/Ethnicity',
            category: 'Asian'
        },
        {
            var: 'absenceTrendRace2',
            id: scopedId('race-ethnicity2'),
            tableId: scopedId('chart-race-ethnicity2'),
            name: 'Race/Ethnicity-Asian/Pacific Islander (Retired Category)',
            demographic: 'Race/Ethnicity',
            category: 'Asian/Pacific Islander (Retired Category)'
        },
        {
            var: 'absenceTrendRace3',
            id: scopedId('race-ethnicity3'),
            tableId: scopedId('chart-race-ethnicity3'),
            name: 'Race/Ethnicity-Black/African American',
            demographic: 'Race/Ethnicity',
            category: 'Black/African American'
        },
        {
            var: 'absenceTrendRace4',
            id: scopedId('race-ethnicity4'),
            tableId: scopedId('chart-race-ethnicity4'),
            name: 'Race/Ethnicity-Hawaiian/Pacific Islander',
            demographic: 'Race/Ethnicity',
            category: 'Hawaiian/Pacific Islander'
        },
        {
            var: 'absenceTrendRace5',
            id: scopedId('race-ethnicity5'),
            tableId: scopedId('chart-race-ethnicity5'),
            name: 'Race/Ethnicity-Latinx',
            demographic: 'Race/Ethnicity',
            category: 'Latinx'
        },
        {
            var: 'absenceTrendRace6',
            id: scopedId('race-ethnicity6'),
            tableId: scopedId('chart-race-ethnicity6'),
            name: 'Race/Ethnicity-Multiracial',
            demographic: 'Race/Ethnicity',
            category: 'Multiracial'
        },
        {
            var: 'absenceTrendRace7',
            id: scopedId('race-ethnicity7'),
            tableId: scopedId('chart-race-ethnicity7'),
            name: 'Race/Ethnicity-Native American',
            demographic: 'Race/Ethnicity',
            category: 'Native American'
        },
        // `type` needs to be added if the `name` can be in more than one category in the data
        {
            var: 'absenceTrendRace8',
            id: scopedId('race-ethnicity8'),
            tableId: scopedId('chart-race-ethnicity8'),
            name: 'Race/Ethnicity-Not Listed',
            demographic: 'Race/Ethnicity',
            category: 'Not Listed'
        },
        {
            var: 'absenceTrendRace9',
            id: scopedId('race-ethnicity9'),
            tableId: scopedId('chart-race-ethnicity9'),
            name: 'Race/Ethnicity-White',
            demographic: 'Race/Ethnicity',
            category: 'White'
        },

        {
            var: 'absenceTrendTLS',
            id: scopedId('temporary-living-situations'),
            tableId: scopedId('chart-temporary-living-situations'),
            name: 'Temporary Living Situation Status-Non-Temporary Living Situation',
            demographic: 'Temporary Living Situation Status',
            category: 'Non-Temporary Living Situation'
        },
        {
            var: 'absenceTrendTLS2',
            id: scopedId('temporary-living-situations2'),
            tableId: scopedId('chart-temporary-living-situations2'),
            name: 'Temporary Living Situation Status-Temporary Living Situation',
            demographic: 'Temporary Living Situation Status',
            category: 'Temporary Living Situation'
        },
    ];


    // @TODO this will eventually get injected from Ep
    function getTooltip(key) {
        const tips = {
            "ALL": "All students enrolled at this school.",
            "Economic Disadvantage": "Students from families who participate in economic assistance programs such as free or reduced lunch.",
            "English Learner Status": "Students who require support to become proficient in English.",
            "Gender": "Gender as identified by the student or parent/guardian.",
            "Grade Level": "The grade of the student.",
            "Grade Entered CPS HS": "The grade that the student entered this school.",
            "IEP Status": "Students who have a current Individualized Education Program (IEP) that outlines the special learning and services the student requires to thrive in school.",
            "Race/Ethnicity": "Race(s) and/or cultural identity the student (or parents/guardians) primarily identifies with.",
            "Temporary Living Situation Status": "Students who are homeless or lack a fixed, regular, and adequate nighttime residence.",
        }
        return tips[key] ?? '';
    }

    // tooltips coming from the CMS bubble information config (getTooltip above is a local category map)
    const getBubbleTooltip = (key) => bubbleConfigs?.[key] ?? '';

    // info bubbles for the "Compare" legend, keyed by dataset label
    const legendTooltips = {
        School: getBubbleTooltip('legendschool'),
        Network: getBubbleTooltip('legendnetwork'),
        District: getBubbleTooltip('legenddistrict'),
    };

    const mappedBubbleConfigFieldKeys =
    {
        "ALL - All Students": "allstudents",
        "Economic Disadvantage - ED": "economicdisadvantage",
        "Economic Disadvantage - Non-ED": "noneconomicdisadvantage",
        "English Learner Status - EL": "englishlearner",
        "English Learner Status - Non-EL": "nonenglishlearner",
        "Gender - Female": "female",
        "Gender - Male": "male",
        "Gender - Non-Binary": "nonbinary",
        "IEP Status - IEP": "iep",
        "IEP Status - Non-IEP": "noniep",
        "Race/Ethenicity - Asian": "asian",
        "Race/Ethenicity - Asian/Pacific Islander (Retired Category)": "asianpacific",
        "Race/Ethenicity - Black/African American": "blackafricanamerican",
        "Race/Ethenicity - Hawaiian/Pacific Islander": "hawaiianpacific",
        "Race/Ethenicity - Latinx": "latinx",
        "Race/Ethenicity - Multiracial": "multiracial",
        "Race/Ethenicity - Native American": "nativeamerican",
        "Race/Ethenicity - Not Listed": "notlisted",
        "Race/Ethenicity - White": "white",
        "Temporary Living Situation Status - Non-STLS": "nontemporaryliving",
        "Temporary Living Situation Status - STLS": "temporaryliving"
    }

    const gradeOrder = ["PE", "PK", "PE/PK", "K", "KG", "1", "01", "2", "02", "3", "03", "4", "04", "5", "05", "6", "06", "7", "07", "8", "08", "9", "09", "10", "11", "12"];


    // only one for now, but the functionality will probably be needed on metrics to come
    //const useDynamicScaling = ["One-Year Dropout Rate"].includes(config.metricTitle);
    const useDynamicScaling = true;

    const tempShaper = (response) => {
        const data = [];
        for (let year of response.SchoolYears) {
            data.push(...year.Categories.map(category => ({
                ...upperizeStringify(category),
                CATEGORY_DESCRIPTION: category.CategoryDescription,
                SCHOOL_YEAR: year.SchoolYear,

                LESSTHAN10: category.Lessthan10 == "" ? "0" : category.Lessthan10,
                NODATA: category.NoData == "" ? "0" : category.NoData,

                NETWORK: response.Network
            })))
        }
        return data.filter(a => a.CATEGORY_DESCRIPTION != null && a.SCHOOL_YEAR != null).sort((a, b) => a.CATEGORY_DESCRIPTION.localeCompare(b.CATEGORY_DESCRIPTION))
            .sort((a, b) => a.SCHOOL_YEAR.localeCompare(b.SCHOOL_YEAR));
    }

    const absenceData = tempShaper(fillMissingDataWithoutNotListed(schoolResponse, GrowthMetricSampleRow));
    const networkData = tempShaper(fillMissingDataWithoutNotListed(networkResponse, GrowthMetricSampleRow));
    const districtData = tempShaper(fillMissingDataWithoutNotListed(districtResponse, GrowthMetricSampleRow));
    console.log('shaped s', absenceData);
    console.log('shaped n', absenceData);
    console.log('shaped d', absenceData);

    // sort after we fill missing. on rewrite, this should be fixed to not rely on weird mutations
    const sortedData = getSortedData();
    if (sortedData["Grade Level"]) {
        const gradeTrends = Object.keys(sortedData["Grade Level"]).map(g => ({
            name: `Grade Level-${g}`,
            grade: g,
            id: scopedId(g),
            tableId: scopedId(`chart-grade-level-${g}`),
            var: g,
            demographic: 'Grade Level',
            category: g
        }));
        getGradeCharts(gradeTrends);
        trends.push(...gradeTrends)
    }

    const mergedData = [
        ...absenceData.map(r => ({ ...r, DATA_SOURCE: 'School' })),
        ...networkData.map(r => ({ ...r, DATA_SOURCE: 'Network' })),
        ...districtData.map(r => ({ ...r, DATA_SOURCE: 'School' })),
    ]

    let network = config.network;
    let schoolName = config.schoolName;
    let absence;
    let rawData = { "School": [], "District": [], "Network": [] };
    let currentYear = '2024-2025';
    let trendLabels = districtResponse.SchoolYears.map(y => y.SchoolYear).sort();
    if (trendLabels.length > 0) currentYear = trendLabels[trendLabels.length - 1];
    console.log('currentyear', currentYear)
    let selectedCategory = "ALL";
    const yearSort = (a, b) => (trendLabels.indexOf(a.SCHOOL_YEAR) - trendLabels.indexOf(b.SCHOOL_YEAR));
    const categorySort = (a, b) => a.CATEGORYDESCRIPTION.localeCompare(b.CATEGORYDESCRIPTION);

    let snapshotLabels = {
        "ALL": getLabels("ALL"),
        "Race/Ethnicity": getLabels("Race/Ethnicity"),
        "Economic Disadvantage": getLabels("Economic Disadvantage"),
        "English Learner Status": getLabels("English Learner Status"),
        "Gender": getLabels("Gender"),
        [gradeField]: getGradeLabels(gradeField),
        "IEP Status": getLabels("IEP Status"),
        "Temporary Living Situation Status": getLabels("Temporary Living Situation Status")
    }

    const snapshotTooltipConfig = {
        enabled: true,
        titleFont: tooltipTitleFont,
        callbacks: {
            title: function (tooltipItem) {
                return ''
            },
            label: function (tooltipItem) {
                let label = tooltipItem.dataset.label;
                let data = rawData[label];
                let match = data.find(value => value.CATEGORYDESCRIPTION === tooltipItem.label && value.SCHOOL_YEAR === currentYear && value.CATEGORY === selectedCategory);
                let line1 = [`${label} ${config.metricTitle} Effect Size: ${tooltipItem.raw}`];
                console.log('tooltip item', tooltipItem)
                console.log('match', match, tooltipItem.label, currentYear, selectedCategory, data);
                let line2 = `${match.EFFECTSIZETEXT}`;
                let line3 = `Total Students: ${Number(match.STUDENTCOUNT).toLocaleString()}`;
                line1.push(line2, line3);
                return line1;
            }
        }
    };

    const trendTooltipConfig = (chartData) => {
        console.log('source data 3', chartData)
        return {
            enabled: true,
            titleFont: tooltipTitleFont,
            callbacks: {
                title: function (tooltipItem) {
                    return ''
                },
                label: (tooltipItem) => {
                    let label = tooltipItem.dataset.label;
                    let data = rawData[label];
                    const yAxisLabel = tooltipItem.label;
                    const match = chartData[yAxisLabel][label.toLowerCase()];
                    let line1 = [`${label} ${config.metricTitle} Effect Size: ${tooltipItem.raw}`];
                    let line2 = `${match.EffectSizeText}`;
                    let line3 = `Total Students: ${Number(match.StudentCount).toLocaleString()}`;
                    line1.push(line2, line3);
                    return line1;
                }
            }
        }
    };

    // this is the inner label
    const snapshotDatalabelConfig = {
        formatter: function (value, context) {
            if (value && value > 0) {
                const entry = rawData[context.dataset.label].find(
                    value => value.SCHOOL_YEAR === currentYear
                        && value.CATEGORY === selectedCategory
                        && value.CATEGORY_DESCRIPTION === snapshotLabels[selectedCategory][context.dataIndex]
                )
                if (entry.LESSTHAN10 != "0") {
                    return entry.LESSTHAN10
                }
                if (entry.NODATA != "0") {
                    return entry.NODATA
                }
                return entry.EFFECTSIZE;
            } else {
                return '';
            }
        },
        display: false,
        color: '#000000',
        font: datalabelFont,
    };

    function getLabels(type) {
        return Array.from(new Set(absenceData.filter((value) =>
            value.CATEGORY === type)
            .map((item) => item.CATEGORY_DESCRIPTION))).sort()
    }

    function getGradeLabels(type) {
        return Array.from(new Set(absenceData.filter((item) =>
            item.CATEGORY === type)
            .map((item) => item.CATEGORY_DESCRIPTION))).sort((a, b) => gradeOrder.indexOf(a) - gradeOrder.indexOf(b));
    }

    // @TODO this should be a simple accessor with the new data structure
    function getSnapshotRaw(label) {
        return rawData[label].filter((value) => value.SCHOOL_YEAR === currentYear);
    }

    function getData(values) {
        return values.sort(categorySort).map(value => {
            if (value.EFFECTSIZE.toLowerCase() === "null") {
                if (value.LESSTHAN10 != "0") {
                    return value.LESSTHAN10;
                } else if (value.NODATA != "0") {
                    return value.NODATA;
                }
            } else {
                return value.EFFECTSIZE;
            }
        })

    }

    // this is currently only used in snapshot
    const getDataset = (type) => {
        const values = rawData[type].filter(r => r.SCHOOL_YEAR == currentYear);
        return {
            data: getData(values),
            labels: values.map(v => v.EFFECTSIZETEXT)
        };
    }


    function getDropoutSnapshotData(label) {
        const schoolData = getDataset('School');
        const networkData = getDataset('Network');
        const districtData = getDataset('District');
        return {
            // Left side labels
            labels: snapshotLabels[label],
            datasets: [
                {
                    label: 'School',
                    data: schoolData.data,
                    backgroundColor: '#50C4D9',
                    sideLabels: schoolData.labels
                },
                {
                    label: 'Network',
                    data: networkData.data,
                    backgroundColor: '#1D48C0',
                    datalabels: {
                        color: 'white',
                        anchor: 'end'
                    },
                    sideLabels: networkData.labels
                },
                {
                    label: 'District',
                    data: districtData.data,
                    backgroundColor: '#061A53',
                    datalabels: {
                        color: 'white',
                        anchor: 'end'
                    },
                    sideLabels: districtData.labels
                }
            ]
        }
    }


    const growthTopLabels = {
        id: 'topLabels',
        afterDatasetsDraw(chart, args, options) {
            const { ctx, scales: { x, y } } = chart;
            let isWide = chart.width > 505;
            if (isWide) {
                ctx.font = "400 14px \"Roboto\", Roboto, Arial, sans-serif";
            } else {
                ctx.font = "400 10px \"Roboto\", Roboto, Arial, sans-serif";
            }
            const yAxis = chart.scales.y;
            let label = yAxis.ticks[0];
            let labelWidth = ctx.measureText(label).width;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            chart.data.datasets.forEach((dataset, index) => {
                if (chart.isDatasetVisible(index)) {
                    dataset.data.forEach((item, i) => {
                        let percent = dataset.data[i];
                        let text = `${dataset.sideLabels[i] != null ? dataset.sideLabels[i] : ''} (${percent != undefined ? percent : 'N/A'})`;
                        if (isNaN(percent)) {
                            if (percent == undefined || percent == null) {
                                percent = 'No Data';
                            }
                            text = percent + ' (N/A)';
                        }
                        let xValue = chart.getDatasetMeta(index).data[i].x;
                        if (xValue === null) {
                            xValue = chart.chartArea.left + labelWidth;
                        }
                        xValue = (chart.chartArea.left + chart.chartArea.right) / 2;
                        const direction = percent > 0 ? 1 : -1; // which side of the bar to label
                        const offset = ((ctx.measureText(text).width + 20) * direction / 2);
                        ctx.fillText(text, xValue - offset, chart.getDatasetMeta(index).data[i].y);
                    })
                }
            })
        }
    }

    function getAllSnapshotConfig(label) {
        const ret = {
            type: 'bar',
            plugins: [ChartDataLabels, legendPad, growthTopLabels, htmlLegendPlugin],
            data: getDropoutSnapshotData(label),
            options: {
                indexAxis: 'y',
                maintainAspectRatio: false,
                onResize: onResize,
                scales: {
                    x: {
                        min: -2.25,
                        max: 2.25,
                        grid: {
                            display: true,
                            color: '#DDE0E7'
                        },
                        border: {
                            dash: [5, 5]
                        },
                        title: {
                            display: true,
                            text: "Effect Size",
                            color: "black"
                        },
                    },
                    y: {
                        grid: {
                            offset: false,
                            display: false
                        },
                        ticks: {
                            color: "black"
                        }
                    }
                },
                plugins: {
                    datalabels: snapshotDatalabelConfig,
                    tooltip: snapshotTooltipConfig,
                    legend: { display: false },
                    htmlLegend: {
                        containerID: `${blockId}-snapshot-growth`,
                        itemTooltips: legendTooltips
                    }
                }
            }
        }

        return ret;
    }


    function changeDropoutDemographic(event) {
        let selected = event.target.value;
        selectedCategory = selected;

        $(parentScope + " .category-title").text(selected);
        const tooltips = $(parentScope + " .category-tooltip");
        tooltips.attr("title", getTooltip(selected));
        tooltips.attr("data-tooltip-content", getTooltip(selected));
        tooltips.each((idx, tooltip) => {
            tooltip._tippy?.setContent(getTooltip(selected));
            infoBubble(tooltip);
        });
        setTrendChart();

        absence.destroy();
        // @TODO mike rawData assignment
        rawData["School"] = absenceData.filter((value) => value.CATEGORY === selected).sort(yearSort);
        rawData["Network"] = networkData.filter((value) => value.CATEGORY === selected &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        rawData["District"] = districtData.filter((value) => value.CATEGORY === selected &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        let $retired = $(`#${scopedId('retired')}`);
        setSnapshotHeight();

        absence = new Chart(
            // @ts-ignore
            document.getElementById(scopedId('absence')),
            getAllSnapshotConfig(selected)
        );
        $retired.addClass("hidden");

        createDropoutTable();
    }

    function createDropoutTable() {
        let labels = snapshotLabels[selectedCategory];
        let previousChart = document.getElementById(scopedId('absenceTable'));
        if (previousChart) {
            previousChart.remove();
        }
        const absenceSnapshotChartBox = document.getElementById(scopedId('absence-chart'));
        const titleRow = $(absenceSnapshotChartBox.parentElement.parentElement).find('.category-title').parent();
        const tipText = getTooltip(selectedCategory)
        const bubble = $(`<span data-toggle="school-profile-tooltip" class="school-info-bubble fa fa-info-circle category-tooltip" data-html="true" data-tooltip-content="${tipText}" aria-label="More Info: Click here" title="${tipText}"></span>`)

        if (!titleRow.find('.school-info-bubble')) {
            titleRow.append(bubble)
            infoBubble(bubble[0]);
        }

        const absenceSnapshotTableDiv = document.createElement('div');
        absenceSnapshotTableDiv.setAttribute('id', scopedId('absenceTable'));
        const table = document.createElement('table');
        table.classList.add('chart-table');
        table.setAttribute('id', scopedId('absence-table'));


        const thead = table.createTHead();
        thead.insertRow(0);

        for (let i = 0; i < labels.length; i++) {
            thead.rows[0].insertCell(i).outerHTML = "<th>" + labels[i] + "</th>";
        }

        thead.rows[0].insertCell(0).classList.add('light-cyan');
        const tbody = table.createTBody();
        tbody.insertRow(0);
        let raw = getSnapshotRaw("School").sort(categorySort);
        raw.forEach((value, i) => {
            let text = raw[i].EFFECTSIZE;
            if (text === "null") {
                if (raw[i].LESSTHAN10 != "0") {
                    tbody.rows[0].insertCell(i).innerText = raw[i].LESSTHAN10;
                } else if (raw[i].NODATA != "0") {
                    tbody.rows[0].insertCell(i).innerText = raw[i].NODATA;
                } else {
                    tbody.rows[0].insertCell(i).innerText = `N/A`;
                }
            } else {
                tbody.rows[0].insertCell(i).innerText = `${text.toLocaleString()}\n${raw[i].EFFECTSIZETEXT}`;
            }
        })
        tbody.insertRow(1);
        raw = getSnapshotRaw("Network").sort(categorySort);

        raw.forEach((value, i) => {
            let text = raw[i].EFFECTSIZE;
            if (text === "null") {
                if (raw[i].LESSTHAN10 != "0") {
                    tbody.rows[1].insertCell(i).innerText = raw[i].LESSTHAN10;
                } else if (raw[i].NODATA != "0") {
                    tbody.rows[1].insertCell(i).innerText = raw[i].NODATA;
                } else {
                    tbody.rows[1].insertCell(i).innerText = `N/A`;
                }
            } else {
                tbody.rows[1].insertCell(i).innerText = `${text.toLocaleString()}\n${raw[i].EFFECTSIZETEXT}`;
            }

        })
        tbody.insertRow(2);
        raw = getSnapshotRaw("District").sort(categorySort);
        raw.forEach((value, i) => {
            let text = raw[i].EFFECTSIZE;

            if (text === "null") {
                if (raw[i].LESSTHAN10 != "0") {
                    tbody.rows[2].insertCell(i).innerText = raw[i].LESSTHAN10;
                } else if (raw[i].NODATA != "0") {
                    tbody.rows[2].insertCell(i).innerText = raw[i].NODATA;
                } else {
                    tbody.rows[2].insertCell(i).innerText = `N/A`;
                }
            } else {
                tbody.rows[2].insertCell(i).innerText = `${text.toLocaleString()}\n${raw[i].EFFECTSIZETEXT}`;
            }

        })

        tbody.rows[0].insertCell(0).outerHTML = "<th scope='row'>School</th>";
        tbody.rows[1].insertCell(0).outerHTML = "<th scope='row'>Network</th>";
        tbody.rows[2].insertCell(0).outerHTML = "<th scope='row'>District</th>";
        //append
        absenceSnapshotChartBox.appendChild(absenceSnapshotTableDiv);
        absenceSnapshotTableDiv.appendChild(table);
    }

    function createSnapshot() {
        rawData["School"] = absenceData.filter((value) => value.CATEGORY === selectedCategory).sort(yearSort);
        rawData["Network"] = networkData.filter((value) => value.CATEGORY === selectedCategory &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        rawData["District"] = districtData.filter((value) => value.CATEGORY === selectedCategory &&
            snapshotLabels[selectedCategory].includes(value.CATEGORY_DESCRIPTION)).sort(yearSort);
        absence = new Chart(
            // @ts-ignore
            document.getElementById(scopedId('absence')),
            getAllSnapshotConfig(selectedCategory)
        );
        $(parentScope + '[data-toggle="school-profile-tooltip"]').each((i, el) => {
            infoBubble(el);
        });

        createDropoutTable();
    }

    function showSnapshot() {
        // check the view dropdown and make sure it's selected on switch
        // bit hacky, but we're refactoring. using aria-posinset just because it's a little more resiliant for now
        const $currentViewType = $(parentScope + ' .absence-trends .tabs-list-container button[aria-selected=true]');
        const ariaPos = $currentViewType.attr('aria-posinset');
        const $newView = $(parentScope + ` .absence-snapshot .tabs-list-container button[aria-posinset=${ariaPos}]`);
        $newView.click();

        $(`#${scopedId('show-trends')}`).removeClass("active");
        $(`#${scopedId('show-snapshot')}`).addClass("active");
        $(parentScope + " .absence-trends").hide();
        $(parentScope + " .absence-snapshot").show();
    }

    function setSnapshotHeight() {
        let height;
        let length = snapshotLabels[selectedCategory].length;
        if (selectedCategory === "Race/Ethnicity") {
            height = length * 200;
        } else {
            height = length * 250;
        }
        let snapshotHeight = document.getElementById(scopedId("snapshot-height"));
        snapshotHeight.style.height = height.toString() + "px";
    }

    $(parentScope + ' .category-title').text("ALL");
    $(parentScope + " .absence-snapshot").hide().removeClass('hidden');

    let absenceSelect = document.getElementById(scopedId("absence-demographics"));
    absenceSelect.addEventListener("change", changeDropoutDemographic);

    createSnapshot();
    setSnapshotHeight();

    $(`#${scopedId('show-snapshot')}`).on("click", showSnapshot);

    function createAllTrendTables() {
        for (const trend of trends) {
            const table = getTrendTable(trend.demographic, trend.category);
            if (!table) continue;
            let categoryChartTableDiv = document.createElement('div');
            // grades were the only one that used to be created dynamically, so we do that here.
            // after refactor, all of them should probably be generated this way (or programmatically in the view)
            if (trend.demographic == 'Grade Level') {
                let parent = document.getElementById(scopedId('absence-grade-tables'));
                let container = document.createElement("div");
                container.classList.add("table-scrolling-wrapper");

                //const tipContent = bubbleConfigs[mappedBubbleConfigFieldKeys[`${gradeField} - ${grade}`]]?.DescriptionHtmlString ?? (notUseDefaultTooltip ? null : gradeTip);
                const tipContent = getTooltip('grade');
                container.innerHTML = tipContent ?
                    `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName?.replaceAll('"', '')}</span> - ${config.metricTitle} Rate - Grade ${trend.category} <span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>
                        <div id="${scopedId('chart-grade-level')}-${trend.category}">&nbsp;</div>` :
                    `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName?.replaceAll('"', '')}</span> - ${config.metricTitle} Rate - Grade ${trend.category} </strong>
                        </p>
                        <div id="${scopedId('chart-grade-level')}-${trend.category}">&nbsp;</div>`;

                parent.appendChild(container)

                container.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {
                    infoBubble(el);
                });
            }
            const categoryChartBox = document.getElementById(trend.tableId);
            categoryChartBox.appendChild(categoryChartTableDiv);

            categoryChartTableDiv.appendChild(table);
        }
    }

    function getTrendTable(demographic, category) {
        // TODO add error checking
        const sourceData = sortedData[demographic][category];
        if (!sourceData) return null;
        const years = Object.keys(sourceData); // TODO check sorting 
        const table = document.createElement('table');
        table.classList.add('chart-table');
        const headRow = document.createElement('tr');
        headRow.appendChild(document.createElement('th')); // spacer
        for (const year of years) {
            const ele = document.createElement('th');
            ele.textContent = year;
            headRow.appendChild(ele);
        }
        table.appendChild(headRow);
        for (const type of ['School', 'Network', 'District']) {
            const row = document.createElement('tr');
            const label = document.createElement('td');
            label.textContent = type;
            row.appendChild(label);
            for (const year of years) {
                const ele = document.createElement('td');

                // find a better way to handle null vs "null" vs empty strings
                const entry = sourceData[year][type.toLowerCase()]
                ele.textContent = "No Data";
                if (entry) {
                    if (entry.Ncegrowth) {
                        ele.textContent = `${entry.Ncegrowth}\n${entry.EffectSizeText}`;
                    } else if (!!entry.Lessthan10) {
                        ele.textContent = entry.Lessthan10
                    } else if (!!entry.NoData) {
                        ele.textContent = entry.NoData
                    }
                }

                row.appendChild(ele);
            }

            table.appendChild(row);
        }

        return table;
    }

    function getTrendChartConfig(demographic, category) {
        // TODO add error checking
        const sourceData = sortedData[demographic][category];
        const labels = Object.keys(sourceData); // TODO check sorting 
        const chartData: ChartData = {
            data: {
                labels: labels,
                datasets: [
                    {
                        label: 'School',
                        data: labels.map(year => sourceData[year].school?.EffectSize),
                        backgroundColor: '#50C4D9',
                        datalabels: {
                            color: 'white'
                        },
                        sideLabels: labels.map(year => sourceData[year].school?.EffectSizeText ?? 'No Data')
                    },
                    {
                        label: 'Network',
                        data: labels.map(year => sourceData[year].network?.EffectSize),
                        backgroundColor: '#1D48C0',
                        hidden: true,
                        datalabels: {
                            color: 'white'
                        },
                        sideLabels: labels.map(year => sourceData[year].network?.EffectSizeText ?? 'No Data')
                    },
                    {
                        label: 'District',
                        data: labels.map(year => sourceData[year].district?.EffectSize),
                        backgroundColor: '#061A53',
                        hidden: true,
                        datalabels: {
                            color: 'white'
                        },
                        sideLabels: labels.map(year => sourceData[year].district?.EffectSizeText ?? 'No Data')
                    },
                ]
            }
        }
        const commonConfig = {

            "type": "bar",
            plugins: [ChartDataLabels, legendPad, growthTopLabels, htmlLegendPlugin],

            "options": {
                "maintainAspectRatio": false,
                "indexAxis": "y",
                "scales": {
                    "x": {
                        "min": -2.25,
                        "max": 2.25,
                        "grid": {
                            "display": true,
                            "color": '#DDE0E7'
                        },
                        "border": {
                            "dash": [5, 5]
                        },
                        "title": {
                            "display": true,
                            "text": "Effect Size",
                            "color": "black"
                        }
                    },
                    "y": {
                        "grid": {
                            "offset": false,
                            "display": false
                        },
                        "ticks": {
                            "color": "black"
                        }
                    }
                },
                plugins: {
                    datalabels: snapshotDatalabelConfig,
                    tooltip: trendTooltipConfig(sourceData),
                    legend: { display: false },
                    htmlLegend: {
                        containerID: generateChartId(category, `${blockId}-trend`),
                        itemTooltips: legendTooltips
                    }
                }
            }
        }
        return deepMerge(chartData, commonConfig);
    }

    function buildTrends() {

        for (const demographic of Object.keys(sortedData)) {// looking like I should switch out of keys vs arrays
            for (const category of Object.keys(sortedData[demographic])) {
                console.group(`Chart config for ${demographic}-${category}`);
                const chartConfig = getTrendChartConfig(demographic, category);
                console.log(chartConfig);
                const key = `${demographic}-${category}`;
                const row = trends.find(t => t.name == key);
                if (row) {
                    console.log('target', document.getElementById(row.id));
                    new Chart(
                        document.getElementById(row.id) as HTMLCanvasElement,
                        chartConfig
                    );
                }
                console.groupEnd();
            }
        }
    }

    // @TODO  when refactoring, all the charts should be built dynamically like this.
    // it would let us decouple a _lot_ from the template and make everything cleaner
    function getGradeCharts(gradesServed) {
        let parent = document.getElementById(scopedId('absence-grade-charts'));

        gradesServed.sort((a, b) => gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade)).forEach((grade) => {
            //const gradeTip = getTooltip(gradeField);
            let container = document.createElement("div");
            container.classList.add("trend-container");

            // @TODO mike this is tooltip stuff that needs to be pulled out
            //const tipContent = bubbleConfigs[mappedBubbleConfigFieldKeys[`${gradeField} - ${grade}`]]?.DescriptionHtmlString ?? (notUseDefaultTooltip ? null : gradeTip);
            const tipContent = getTooltip('grade');
            container.innerHTML = tipContent ?
                `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName?.replaceAll('"', '')}</span> - ${config.metricTitle} Rate - Grade ${grade.grade} <span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>` :
                `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName?.replaceAll('"', '')}</span> - ${config.metricTitle} Rate - Grade ${grade.grade} </strong>
                        </p>`;

            let canvasDiv = document.createElement('canvas');
            canvasDiv.setAttribute('id', grade.id);
            container.appendChild(canvasDiv);
            parent.appendChild(container);
        });
        const tipContent = getTooltip('grade');
        parent.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {

            infoBubble(el);
        });
    }

    buildTrends()

    function showTrends() {
        // check the view dropdown and make sure it's selected on switch
        // bit hacky, but we're refactoring. using aria-posinset just because it's a little more resiliant for now
        const $currentViewType = $(parentScope + ' .absence-snapshot .tabs-list-container button[aria-selected=true]');
        const ariaPos = $currentViewType.attr('aria-posinset');
        const $newView = $(parentScope + ` .absence-trends .tabs-list-container button[aria-posinset=${ariaPos}]`);
        $newView.click();

        $(`#${scopedId('show-snapshot')}`).removeClass("active");
        $(`#${scopedId('show-trends')}`).addClass("active");

        $(parentScope + " .absence-snapshot").hide();
        $(parentScope + " .absence-trends").show();
    }

    function hideTrendCharts() {
        $(parentScope).find("div.absence-all, div.absence-economic,div.absence-english,div.absence-gender,div.absence-grade,div.absence-iep,div.absence-race,div.absence-temporary").hide();
    }

    function setTrendChart() {
        hideTrendCharts();
        switch (selectedCategory) {
            case "ALL":
                $(parentScope).find("div.absence-all").show();
                break;
            case "Race/Ethnicity":
                $(parentScope).find("div.absence-race").show();
                break;
            case "Economic Disadvantage":
                $(parentScope).find("div.absence-economic").show();
                break;
            case "English Learner Status":
                $(parentScope).find("div.absence-english").show();
                break;
            case "Gender":
                $(parentScope).find("div.absence-gender").show();
                break;
            case gradeField:
                $(parentScope).find("div.absence-grade").show();
                break;
            case "IEP Status":
                $(parentScope).find("div.absence-iep").show();
                break;
            case "Temporary Living Situation Status":
                $(parentScope).find("div.absence-temporary").show();
                break;
        }
    }

    $(`#${scopedId('show-trends')}`).on("click", showTrends);
    $(`#${scopedId('show-snapshot')}`).on("click", showSnapshot);

    $(document).ready(function () {
        setTrendChart();
        createAllTrendTables();
        showTrends();
    });
});
